<script setup lang="ts">
import { ScalarFormInputGroup, ScalarToggleInput } from '@scalar/components'
import type { ApiReferenceConfiguration } from '@scalar/types/api-reference'

import { useI18n } from '@/i18n'

type LayoutOptions = {
  showSidebar?: boolean
  defaultOpenFirstTag?: boolean
  defaultOpenAllTags?: boolean
  expandAllModelSections?: boolean
  expandAllResponses?: boolean
  hideClientButton?: boolean
  hideDarkModeToggle?: boolean
  hideModels?: boolean
  hideSearch?: boolean
  hideTestRequestButton?: boolean
  showOperationId?: boolean
}

const { configuration } = defineProps<{
  configuration?: Partial<ApiReferenceConfiguration>
}>()

const model = defineModel<LayoutOptions>({
  default: {},
})

const { t } = useI18n()

function getValue(key: keyof LayoutOptions, defaultValue: boolean = false) {
  return model.value[key] ?? configuration?.[key] ?? defaultValue
}

function setValue(
  key: keyof LayoutOptions,
  value: boolean,
  defaultValue: boolean = false,
) {
  if (value !== defaultValue) {
    model.value = { ...model.value, [key]: value }
  } else {
    model.value = Object.fromEntries(
      Object.entries(model.value).filter(([k]) => key !== k),
    )
  }
}
</script>
<template>
  <ScalarFormInputGroup>
    <ScalarToggleInput
      :modelValue="getValue('showSidebar', true)"
      @update:modelValue="(v) => setValue('showSidebar', !!v, true)">
      {{ t('developerTools.showSidebar') }}
    </ScalarToggleInput>
    <ScalarToggleInput
      :modelValue="getValue('defaultOpenFirstTag', true)"
      @update:modelValue="(v) => setValue('defaultOpenFirstTag', !!v, true)">
      {{ t('developerTools.defaultOpenFirstTag') }}
    </ScalarToggleInput>
    <ScalarToggleInput
      :modelValue="getValue('defaultOpenAllTags')"
      @update:modelValue="(v) => setValue('defaultOpenAllTags', !!v)">
      {{ t('developerTools.defaultOpenAllTags') }}
    </ScalarToggleInput>
    <ScalarToggleInput
      :modelValue="getValue('expandAllModelSections')"
      @update:modelValue="(v) => setValue('expandAllModelSections', !!v)">
      {{ t('developerTools.expandAllModelSections') }}
    </ScalarToggleInput>
    <ScalarToggleInput
      :modelValue="getValue('expandAllResponses')"
      @update:modelValue="(v) => setValue('expandAllResponses', !!v)">
      {{ t('developerTools.expandAllResponses') }}
    </ScalarToggleInput>
    <ScalarToggleInput
      :modelValue="getValue('hideClientButton')"
      @update:modelValue="(v) => setValue('hideClientButton', !!v)">
      {{ t('developerTools.hideClientButton') }}
    </ScalarToggleInput>
    <ScalarToggleInput
      :modelValue="getValue('hideDarkModeToggle')"
      @update:modelValue="(v) => setValue('hideDarkModeToggle', !!v)">
      {{ t('developerTools.hideDarkModeToggle') }}
    </ScalarToggleInput>
    <ScalarToggleInput
      :modelValue="getValue('hideModels')"
      @update:modelValue="(v) => setValue('hideModels', !!v)">
      {{ t('developerTools.hideModels') }}
    </ScalarToggleInput>
    <ScalarToggleInput
      :modelValue="getValue('hideSearch')"
      @update:modelValue="(v) => setValue('hideSearch', !!v)">
      {{ t('developerTools.hideSearch') }}
    </ScalarToggleInput>
    <ScalarToggleInput
      :modelValue="getValue('showOperationId')"
      @update:modelValue="(v) => setValue('showOperationId', !!v)">
      {{ t('developerTools.showOperationId') }}
    </ScalarToggleInput>
    <ScalarToggleInput
      :modelValue="getValue('hideTestRequestButton')"
      @update:modelValue="(v) => setValue('hideTestRequestButton', !!v)">
      {{ t('developerTools.hideTestRequestButton') }}
    </ScalarToggleInput>
  </ScalarFormInputGroup>
</template>
